import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

interface ImageLightboxProps {
  urls: string[];
  initialIndex?: number;
  onClose: () => void;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;

export function ImageLightbox({ urls, initialIndex = 0, onClose }: ImageLightboxProps) {
  const [index, setIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastClickTime = useRef<number>(0);

  const resetZoomPan = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const clampZoom = (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

  const prev = useCallback(() => {
    setIndex((i) => (i - 1 + urls.length) % urls.length);
    resetZoomPan();
  }, [urls.length, resetZoomPan]);

  const next = useCallback(() => {
    setIndex((i) => (i + 1) % urls.length);
    resetZoomPan();
  }, [urls.length, resetZoomPan]);

  // 키보드 이벤트
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === '+' || e.key === '=') {
        setZoom((z) => clampZoom(z + ZOOM_STEP));
      } else if (e.key === '-') {
        setZoom((z) => {
          const next = clampZoom(z - ZOOM_STEP);
          if (next <= 1) setPan({ x: 0, y: 0 });
          return next;
        });
      } else if (e.key === '0') {
        resetZoomPan();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, prev, next, resetZoomPan]);

  // 마우스 휠 줌
  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
    setZoom((z) => {
      const next = clampZoom(z + delta);
      if (next <= 1) setPan({ x: 0, y: 0 });
      return next;
    });
  }, []);

  // 드래그 패닝
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom <= 1) return;
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  }, [zoom, pan]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !dragStart.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setPan({ x: dragStart.current.panX + dx, y: dragStart.current.panY + dy });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    dragStart.current = null;
  }, []);

  // 더블 클릭 줌
  const handleImageClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const now = Date.now();
    if (now - lastClickTime.current < 300) {
      // 더블 클릭
      if (zoom > 1) {
        resetZoomPan();
      } else {
        setZoom(2);
      }
    }
    lastClickTime.current = now;
  }, [zoom, resetZoomPan]);

  const zoomPercent = Math.round(zoom * 100);
  const hasMultiple = urls.length > 1;

  const zoomIn = (e: React.MouseEvent) => {
    e.stopPropagation();
    setZoom((z) => clampZoom(z + ZOOM_STEP));
  };

  const zoomOut = (e: React.MouseEvent) => {
    e.stopPropagation();
    setZoom((z) => {
      const next = clampZoom(z - ZOOM_STEP);
      if (next <= 1) setPan({ x: 0, y: 0 });
      return next;
    });
  };

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    resetZoomPan();
  };

  return createPortal(
    <div
      ref={containerRef}
      className="fixed inset-0 bg-black z-[10000] flex items-center justify-center overflow-hidden"
      onClick={onClose}
      onWheel={handleWheel}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
    >
      {/* 닫기 버튼 */}
      <button
        className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
        onClick={onClose}
        aria-label="닫기"
      >
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* 이미지 */}
      <img
        src={urls[index]}
        alt="첨부 이미지"
        draggable={false}
        className="max-w-full max-h-full object-contain select-none"
        style={{
          transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
          transformOrigin: 'center center',
          transition: isDragging ? 'none' : 'transform 0.15s ease',
          cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
        onClick={handleImageClick}
        onMouseDown={handleMouseDown}
      />

      {/* 줌 컨트롤 */}
      <div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/60 rounded-full px-3 py-1.5"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="text-white hover:text-gray-300 w-7 h-7 flex items-center justify-center rounded hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={zoomOut}
          disabled={zoom <= MIN_ZOOM}
          aria-label="축소"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>

        <span className="text-white text-xs font-mono w-10 text-center">{zoomPercent}%</span>

        <button
          className="text-white hover:text-gray-300 w-7 h-7 flex items-center justify-center rounded hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={zoomIn}
          disabled={zoom >= MAX_ZOOM}
          aria-label="확대"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>

        {zoom !== 1 && (
          <>
            <div className="w-px h-4 bg-white/30" />
            <button
              className="text-white hover:text-gray-300 text-xs px-1.5 py-0.5 rounded hover:bg-white/10"
              onClick={handleReset}
              aria-label="맞추기"
            >
              맞추기
            </button>
          </>
        )}

        {/* 복수 이미지일 때 페이지 표시 */}
        {hasMultiple && (
          <>
            <div className="w-px h-4 bg-white/30" />
            <span className="text-white text-xs">
              {index + 1} / {urls.length}
            </span>
          </>
        )}
      </div>

      {/* 이전 버튼 */}
      {hasMultiple && (
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/40 rounded-full p-2"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          aria-label="이전 이미지"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* 다음 버튼 */}
      {hasMultiple && (
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/40 rounded-full p-2"
          onClick={(e) => { e.stopPropagation(); next(); }}
          aria-label="다음 이미지"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>,
    document.body,
  );
}
